import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Vehicle } from '../types/vehicle';
const STORAGE_KEY = 'flashgo_vehicles';
interface VehiclesContextValue {
  vehicles: Vehicle[];
  addVehicle: (vehicle: Omit<Vehicle, 'id'>) => void;
  removeVehicle: (id: string) => void;
  isAdminOpen: boolean;
  openAdmin: () => void;
  closeAdmin: () => void;
}
const VehiclesContext = createContext<VehiclesContextValue | null>(null);
function readStorage(): Vehicle[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as Vehicle[] : [];
  } catch {
    return [];
  }
}
export function VehiclesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  useEffect(() => {
    setVehicles(readStorage());
  }, []);
  const persist = (next: Vehicle[]) => {
    setVehicles(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage full or unavailable — keep in-memory state */
    }
  };
  const value = useMemo<VehiclesContextValue>(() => ({
    vehicles,
    addVehicle: vehicle => persist([{
      ...vehicle,
      id: `v_${Date.now().toString(36)}`
    }, ...vehicles]),
    removeVehicle: id => persist(vehicles.filter(v => v.id !== id)),
    isAdminOpen,
    openAdmin: () => setIsAdminOpen(true),
    closeAdmin: () => setIsAdminOpen(false)
  }), [vehicles, isAdminOpen]);
  return <VehiclesContext.Provider value={value} data-id="element-547" data-mp="252ozcnfo05t4">
      {children}
    </VehiclesContext.Provider>;
}
export function useVehicles(): VehiclesContextValue {
  const ctx = useContext(VehiclesContext);
  if (!ctx) {
    throw new Error('useVehicles must be used within a VehiclesProvider');
  }
  return ctx;
}